import { types, Instance } from 'mobx-state-tree';

/**
 * Cursor data for a user on a mindmap
 */
const CursorData = types.model('CursorData', {
  userId: types.string,
  userName: types.string,
  userColor: types.string,
  x: types.number,         // Flow coordinates (not screen)
  y: types.number,         // Flow coordinates (not screen)
  mindmapId: types.string,
  timestamp: types.number,
});

/**
 * MindmapCursorStore - Tracks remote user cursors for the current mindmap
 * 
 * Purpose:
 * - Store cursor positions from other users
 * - Automatically clean up stale cursors (>10s old)
 * - Provide cursor data for rendering
 * 
 * Usage:
 * - WorkspaceRealtimeStore forwards cursor:update events here
 * - useCursorState hook reads cursors for rendering
 * - Cursors component displays the cursors on canvas
 */
export const MindmapCursorStore = types
  .model('MindmapCursorStore', {
    cursors: types.map(CursorData), // userId -> CursorData
  })
  .views(self => ({
    /**
     * Get all active cursors (excluding stale ones)
     */
    get activeCursors() {
      const now = Date.now();
      const staleThreshold = 10000; // 10 seconds
      
      return Array.from(self.cursors.values()).filter(cursor => {
        const age = now - cursor.timestamp;
        return age < staleThreshold;
      });
    },
    
    /**
     * Get cursor for specific user
     */
    getCursor(userId: string) {
      return self.cursors.get(userId);
    },
    
    /**
     * Get all cursors for a specific mindmap
     */
    getCursorsForMindmap(mindmapId: string) {
      return this.activeCursors.filter(cursor => cursor.mindmapId === mindmapId);
    },
  }))
  .actions(self => ({
    /**
     * Update cursor position for a user
     */
    updateCursor(cursorData: {
      userId: string;
      userName: string;
      userColor: string;
      x: number;
      y: number;
      mindmapId: string;
      timestamp: number;
    }) {
      // Check if cursor already exists
      const existing = self.cursors.get(cursorData.userId);
      
      if (existing) {
        // Update existing cursor
        existing.x = cursorData.x;
        existing.y = cursorData.y;
        existing.timestamp = cursorData.timestamp;
      } else {
        // Add new cursor
        self.cursors.set(cursorData.userId, {
          userId: cursorData.userId,
          userName: cursorData.userName,
          userColor: cursorData.userColor,
          x: cursorData.x,
          y: cursorData.y,
          mindmapId: cursorData.mindmapId,
          timestamp: cursorData.timestamp,
        });
      }
    },
    
    /**
     * Remove cursor for a user
     */
    removeCursor(userId: string) {
      self.cursors.delete(userId);
    },
    
    /**
     * Remove all cursors for a mindmap
     */
    clearMindmapCursors(mindmapId: string) {
      self.cursors.forEach((cursor, userId) => {
        if (cursor.mindmapId === mindmapId) {
          self.cursors.delete(userId);
        }
      });
    },
    
    /**
     * Remove stale cursors (older than 10 seconds)
     * Called periodically by a cleanup interval
     */
    removeStaleCursors() {
      const now = Date.now();
      const staleThreshold = 10000; // 10 seconds
      
      const staleUserIds: string[] = [];
      
      self.cursors.forEach((cursor, userId) => {
        if (now - cursor.timestamp > staleThreshold) {
          staleUserIds.push(userId);
        }
      });
      
      staleUserIds.forEach(userId => {
        self.cursors.delete(userId);
      });
      
      if (staleUserIds.length > 0) {
        console.log(`🧹 [MindmapCursor] Removed ${staleUserIds.length} stale cursors`);
      }
    },
    
    /**
     * Clear all cursors
     */
    clearAll() {
      self.cursors.clear();
    },
  }));

export type IMindmapCursorStore = Instance<typeof MindmapCursorStore>;

